// src/pages/citizen/Profile.tsx
import React, { useState, useEffect } from 'react';
import { 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar, 
  Shield, 
  CheckCircle, 
  Edit,
  Save,
  X,
  Eye,
  EyeOff
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

const CitizenProfile: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    idNumber: '',
    gender: '',
    dateOfBirth: '',
    physicalAddress: '',
    voterRegistrationNumber: '',
    pollingStation: '',
    languagePreference: 'ENGLISH',
    receiveSmsAlerts: true,
    receiveEmailAlerts: false,
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      // For demo, we'll use mock data. In production, use:
      // const response = await apiService.getCitizenProfile();
      const mockProfile = {
        id: 1,
        phoneNumber: '0712345678',
        fullName: 'John Doe',
        email: 'john.doe@example.com',
        idNumber: '12345678',
        gender: 'MALE',
        dateOfBirth: '1990-01-01',
        age: 34,
        languagePreference: 'ENGLISH',
        wardName: 'Biashara Ward',
        constituencyName: 'Nakuru Town East',
        physicalAddress: '123 Main Street, Nakuru',
        voterRegistrationNumber: 'V123456789',
        pollingStation: 'Biashara Primary School',
        receiveSmsAlerts: true,
        receiveEmailAlerts: true,
        verified: true,
        createdAt: '2024-01-01',
        reportCount: 12,
        active: true,
      };

      setProfile(mockProfile);
      setFormData({
        fullName: mockProfile.fullName,
        email: mockProfile.email,
        phoneNumber: mockProfile.phoneNumber,
        idNumber: mockProfile.idNumber,
        gender: mockProfile.gender,
        dateOfBirth: mockProfile.dateOfBirth,
        physicalAddress: mockProfile.physicalAddress,
        voterRegistrationNumber: mockProfile.voterRegistrationNumber,
        pollingStation: mockProfile.pollingStation,
        languagePreference: mockProfile.languagePreference,
        receiveSmsAlerts: mockProfile.receiveSmsAlerts,
        receiveEmailAlerts: mockProfile.receiveEmailAlerts,
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // In production, use:
      // await apiService.updateProfile(profile.id, formData);
      toast.success('Profile updated successfully');
      setIsEditing(false);
      fetchProfile(); // Refresh data
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: profile.fullName,
      email: profile.email,
      phoneNumber: profile.phoneNumber,
      idNumber: profile.idNumber,
      gender: profile.gender,
      dateOfBirth: profile.dateOfBirth,
      physicalAddress: profile.physicalAddress,
      voterRegistrationNumber: profile.voterRegistrationNumber,
      pollingStation: profile.pollingStation,
      languagePreference: profile.languagePreference,
      receiveSmsAlerts: profile.receiveSmsAlerts,
      receiveEmailAlerts: profile.receiveEmailAlerts,
    });
    setIsEditing(false);
  };

  const InfoField = ({ label, value, icon: Icon }: any) => (
    <div className="flex items-start py-3 border-b border-gray-200">
      <Icon className="text-gray-400 mt-1 mr-3" size={20} />
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <p className="mt-1 text-[#0b2045]">{value || 'Not provided'}</p>
      </div>
    </div>
  );

  const EditField = ({ label, name, type = 'text', placeholder, options }: any) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      {type === 'select' ? (
        <select
          name={name}
          value={formData[name as keyof typeof formData] as string}
          onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
        >
          {options?.map((option: any) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : type === 'checkbox' ? (
        <div className="flex items-center">
          <input
            type="checkbox"
            name={name}
            checked={formData[name as keyof typeof formData] as boolean}
            onChange={(e) => setFormData({ ...formData, [name]: e.target.checked })}
            className="h-4 w-4 text-[#3c8d2e] focus:ring-[#3c8d2e] border-gray-300 rounded"
          />
          <label className="ml-2 text-sm text-gray-700">
            {placeholder || `Enable ${label.toLowerCase()}`}
          </label>
        </div>
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name as keyof typeof formData] as string}
          onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
          placeholder={placeholder}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0b2045]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0b2045]">My Profile</h1>
            <p className="text-gray-600 mt-2">Manage your personal information and preferences</p>
          </div>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center px-4 py-2 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] transition-colors"
            >
              <Edit size={16} className="mr-2" />
              Edit Profile
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-[#f4f7f6] transition-colors"
              >
                <X size={16} className="mr-2" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="inline-flex items-center px-4 py-2 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022] transition-colors"
              >
                <Save size={16} className="mr-2" />
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Verification Status */}
      <div className={`mb-6 p-4 rounded-lg ${
        profile?.verified 
          ? 'bg-[#3c8d2e]/10 border border-[#3c8d2e]/30' 
          : 'bg-[#fefce8] border border-[#ca8a04]/30'
      }`}>
        <div className="flex items-center">
          <div className={`p-2 rounded-full ${profile?.verified ? 'bg-[#3c8d2e]/20' : 'bg-[#fefce8]'} mr-3`}>
            {profile?.verified ? (
              <CheckCircle className="text-[#3c8d2e]" size={20} />
            ) : (
              <Shield className="text-[#ca8a04]" size={20} />
            )}
          </div>
          <div>
            <h3 className="font-medium text-[#0b2045]">
              {profile?.verified ? 'Account Verified' : 'Account Not Verified'}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {profile?.verified 
                ? 'Your account has been verified. You can access all features.'
                : 'Please complete verification to access all features.'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
            <h2 className="text-lg font-semibold text-[#0b2045] mb-6">Personal Information</h2>
            
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <EditField
                    label="Full Name"
                    name="fullName"
                    placeholder="Enter your full name"
                  />
                  <EditField
                    label="ID Number"
                    name="idNumber"
                    placeholder="National ID number"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <EditField
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="your.email@example.com"
                  />
                  <EditField
                    label="Phone Number"
                    name="phoneNumber"
                    type="tel"
                    placeholder="0712 345 678"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <EditField
                    label="Gender"
                    name="gender"
                    type="select"
                    options={[
                      { value: 'MALE', label: 'Male' },
                      { value: 'FEMALE', label: 'Female' },
                      { value: 'OTHER', label: 'Other' },
                    ]}
                  />
                  <EditField
                    label="Date of Birth"
                    name="dateOfBirth"
                    type="date"
                  />
                </div>

                <EditField
                  label="Physical Address"
                  name="physicalAddress"
                  placeholder="Street, City, County"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <EditField
                    label="Voter Registration Number"
                    name="voterRegistrationNumber"
                    placeholder="Voter ID number"
                  />
                  <EditField
                    label="Polling Station"
                    name="pollingStation"
                    placeholder="Your polling station"
                  />
                </div>

                <EditField
                  label="Language Preference"
                  name="languagePreference"
                  type="select"
                  options={[
                    { value: 'ENGLISH', label: 'English' },
                    { value: 'KISWAHILI', label: 'Kiswahili' },
                    { value: 'BILINGUAL', label: 'Bilingual' },
                  ]}
                />

                <div className="space-y-3">
                  <EditField
                    label="Receive SMS Alerts"
                    name="receiveSmsAlerts"
                    type="checkbox"
                    placeholder="Receive important updates via SMS"
                  />
                  <EditField
                    label="Receive Email Alerts"
                    name="receiveEmailAlerts"
                    type="checkbox"
                    placeholder="Receive important updates via email"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <InfoField label="Full Name" value={profile?.fullName} icon={User} />
                <InfoField label="Phone Number" value={profile?.phoneNumber} icon={Phone} />
                <InfoField label="Email" value={profile?.email} icon={Mail} />
                <InfoField label="ID Number" value={profile?.idNumber} icon={Shield} />
                <InfoField label="Gender" value={profile?.gender} icon={User} />
                <InfoField label="Date of Birth" value={profile?.dateOfBirth} icon={Calendar} />
                <InfoField label="Age" value={profile?.age?.toString()} icon={Calendar} />
                <InfoField label="Physical Address" value={profile?.physicalAddress} icon={MapPin} />
                <InfoField label="Voter Registration" value={profile?.voterRegistrationNumber} icon={Shield} />
                <InfoField label="Polling Station" value={profile?.pollingStation} icon={MapPin} />
                <InfoField label="Ward" value={profile?.wardName} icon={MapPin} />
                <InfoField label="Constituency" value={profile?.constituencyName} icon={MapPin} />
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Account Stats */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
            <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Account Statistics</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Reports Submitted</span>
                <span className="font-bold text-[#0b2045]">{profile?.reportCount || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Reports Resolved</span>
                <span className="font-bold text-[#3c8d2e]">8</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Response Rate</span>
                <span className="font-bold text-[#0b2045]">75%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Member Since</span>
                <span className="font-medium text-[#0b2045]">
                  {new Date(profile?.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
            <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Preferences</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Language</span>
                <span className="font-medium text-[#0b2045]">{profile?.languagePreference}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">SMS Alerts</span>
                <span className={`px-2 py-1 text-xs rounded ${
                  profile?.receiveSmsAlerts 
                    ? 'bg-[#3c8d2e]/10 text-[#3c8d2e]' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {profile?.receiveSmsAlerts ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Email Alerts</span>
                <span className={`px-2 py-1 text-xs rounded ${
                  profile?.receiveEmailAlerts 
                    ? 'bg-[#3c8d2e]/10 text-[#3c8d2e]' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {profile?.receiveEmailAlerts ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Account Status</span>
                <span className={`px-2 py-1 text-xs rounded ${
                  profile?.active 
                    ? 'bg-[#3c8d2e]/10 text-[#3c8d2e]' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {profile?.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
            <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] transition-colors">
                Download Reports
              </button>
              <button className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] transition-colors">
                View Activity History
              </button>
              <button className="w-full text-left px-3 py-2 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] transition-colors text-red-600">
                Request Account Deletion
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CitizenProfile;